import React, { memo, useMemo, useState, useCallback, useEffect } from 'react';
import ReactCountryFlag from 'react-country-flag';
import classNames from 'classnames';
import loadable from '@loadable/component';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import { ensureListing, ensureUser, cleanPlaceholderText } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';

import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  IconReviewStar,
  Button,
  Modal,
} from '../../components';

import css from './ListingCard.module.css';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import { levelsList, getExperienceLabel, getYearsFromListing, getAvailableTeachingLanguages, getTutorPriceBand } from '../../util/constants';
import { PRICEMULTIPLIER } from '../../util/types';
import { convertCurrency, convertUSDToSelected, getCurrencySymbol } from '../../util/currencyConversion';
import { fetchFxRates } from '../../util/api';

// Use ResponsiveImage directly for priority images, LazyImage for others
const LazyImage = lazyLoadWithDimensions(ResponsiveImage);
const InquiryForm = loadable(
  () =>
    import(
      /* webpackChunkName: "ListingCardInquiryForm" */ '../../containers/ListingPage/InquiryForm/InquiryForm'
    ),
  { fallback: null }
);

const PriceMaybe = memo(props => {
  const { price, publicData, intl, listingTypeConfig, listing, author } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  // Convert money amount to a number and show as credits
  const credits = price ? convertMoneyToNumber(price) : null;
  if (credits == null) return null;

  // Remove .0 if it's a whole number
  const formattedCredits =
    Number.isInteger(credits)
      ? (credits * PRICEMULTIPLIER).toString()
      : (credits * PRICEMULTIPLIER).toFixed(1);

  const selectedCurrency = useSelector(state => state.currency.selectedCurrency);
  const fxRates = useSelector(state => state.currency.fxRates);

  // Convert listing native price (EUR/USD/AED) to selected user currency
  const basePrice = price ? convertMoneyToNumber(price) : null;
  if (basePrice == null) return null;

  // Defensive handling for legacy non-USD (e.g. AED) listings that fall outside agreed bands:
  // If a tutor listing was stored with currency 'AED' but the nominal number matches their agreed USD band
  // (e.g. 22 AED for a $15-$22 band, or 50 AED for a $41-$50 band), interpret base price directly in USD.
  let effectiveBasePrice = basePrice;
  let effectiveSourceCurrency = price?.currency || 'USD';

  if (listing) {
    const tutorBand = getTutorPriceBand(listing, author);
    if (tutorBand && effectiveSourceCurrency === 'AED') {
      if (basePrice >= tutorBand.min && basePrice <= tutorBand.max) {
        effectiveSourceCurrency = 'USD';
      } else {
        const rawInUSD = convertCurrency(basePrice, 'AED', 'USD', fxRates).amount;
        if (rawInUSD < tutorBand.min) {
          effectiveBasePrice = tutorBand.min;
          effectiveSourceCurrency = 'USD';
        } else if (rawInUSD > tutorBand.max) {
          effectiveBasePrice = tutorBand.max;
          effectiveSourceCurrency = 'USD';
        }
      }
    }
  }

  const conv = convertCurrency(effectiveBasePrice, effectiveSourceCurrency, selectedCurrency, fxRates);
  // Always round up any fractional value to the upper whole number (e.g. 183.01 -> 184)
  const priceAmountText = Math.ceil(conv.amount).toString();

  const priceValMarkup = <span className={css.creditNumber}>{priceAmountText}</span>;

  const displaySymbol = getCurrencySymbol(selectedCurrency).trim();

  return (
    <div
      className={css.priceSingleLine}
    >
      <span className={css.pricePart}>
        {displaySymbol} {priceValMarkup}
      </span>
    </div>
  );
});

const AuthorBioSection = memo(({ bio }) => {
  const [expanded, setExpanded] = useState(false);
  const { text, isLong } = useMemo(() => {
    if (typeof bio !== 'string') return { text: '', isLong: false };
    const trimmed = bio.trim();
    if (trimmed.length > 140) {
      return { text: expanded ? trimmed : trimmed.slice(0, 140) + '...', isLong: true };
    }
    return { text: trimmed, isLong: false };
  }, [bio, expanded]);

  return (
    <div className={css.authorBio}>
      <div className={css.listingDescription}>
        {text}
        {isLong ? (
          <button type="button" onClick={() => setExpanded(e => !e)} className={css.seeMoreLess}>
            {expanded ? (
              <FormattedMessage id="ListingCard.showLess" />
            ) : (
              <FormattedMessage id="ListingCard.showMore" />
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
});

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    showListingImage,
    style,
    priorityImage,
  } = props;

  // Use author's profile image instead of listing's first image
  const authorFromListing = ensureUser(currentListing.author);
  const profileImage = authorFromListing?.profileImage || null;
  const variants = profileImage ? Object.keys(profileImage?.attributes?.variants || {}) : [];
  const ImageComponent = priorityImage ? ResponsiveImage : LazyImage;
  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={profileImage}
        variants={variants}
        sizes={renderSizes}
        priority={priorityImage}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
const ListingCardComponent = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const [playVideo, setVideo] = useState(false);
  const [isInquiryModalOpen, setInquiryModalOpen] = useState(false);

  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const currentUser = useSelector(state => state.user.currentUser);

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    reviewSummaries,
    priorityImage = false,
    // Optional props for Inquiry modal context
    onSubmitInquiry,
    onManageDisableScrolling,
    sendInquiryError,
    sendInquiryInProgress,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const { bio } = author?.attributes?.profile || {};

  const authorName = author.attributes.profile.displayName;
  const { country, countryOfOrigin } = author?.attributes?.profile?.publicData || {};
  const {
    listingType,
    professionalBadge,
    cardStyle,
    aboutMe,
    spokenLanguage = [],
    teachingLanguages = [],
    levels = [],
    ratingAvg,
    ratingCount,
    videoIntroduction,
  } = publicData || {};

  // Check teachFromZero capability or arabic_from_scratch specialization (Item 2.9)
  const authorProfilePublicData = author?.attributes?.profile?.publicData || {};
  const specializations = publicData?.specializations || authorProfilePublicData?.specializations || [];
  const teachFromZero =
    publicData?.teachFromZero === 'true' ||
    publicData?.teachFromZero === true ||
    publicData?.teachFromZero === 'yes' ||
    authorProfilePublicData?.teachFromZero === 'true' ||
    authorProfilePublicData?.teachFromZero === true ||
    authorProfilePublicData?.teachFromZero === 'yes' ||
    (Array.isArray(specializations) && specializations.includes('arabic_from_scratch'));

  // Clean out template placeholder text so it does not render as live copy (Item 2.2)
  const displayBio = cleanPlaceholderText(aboutMe) || cleanPlaceholderText(bio);

  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);
  const pageLang = (intl?.locale || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  
  const experienceLabel = useMemo(() => {
    const years = getYearsFromListing(listing);
    const pubData = listing?.attributes?.publicData || {};
    const authorPubData = author?.attributes?.profile?.publicData || {};
    const isCertified =
      pubData.priceBand === 'arabibi_certified' ||
      authorPubData.priceBand === 'arabibi_certified' ||
      pubData.adminPriceBand === 'arabibi_certified';
    // Experience strictly sets the tier; teaching categories do not override or lower earned tier
    return getExperienceLabel(years, intl, { isCertified });
  }, [listing, author, intl]);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card-2x',
  } = config.layout.listingImage;

  const ratingText = ratingAvg > 0 ? ratingAvg.toFixed(1) : '0.0';

  // Memoize spoken language labels to prevent unnecessary recalculations
  const language = (intl?.locale || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const spokenLanguageLabels = useMemo(
    () =>
      (spokenLanguage || [])
        .map(l => (language === 'en' ? l?.label : l?.label_ar))
        .filter(Boolean)
        .join(', '),
    [language, spokenLanguage]
  );
  
  const spokenLanguageLabelsMobile = useMemo(
    () =>
      (spokenLanguage || [])
        .map(l => l?.label)
        .filter(Boolean)
        .join(', '),
    [spokenLanguage]
  );

  const rawTeachingLanguages = useMemo(() => {
    if (Array.isArray(teachingLanguages) && teachingLanguages.length > 0) {
      return teachingLanguages;
    }
    if (typeof teachingLanguages === 'string' && teachingLanguages.trim()) {
      return [teachingLanguages.trim()];
    }

    const authorPublic =
      author?.attributes?.profile?.publicData ||
      author?.attributes?.publicData ||
      {};
    if (Array.isArray(authorPublic.teachingLanguages) && authorPublic.teachingLanguages.length > 0) {
      return authorPublic.teachingLanguages;
    }
    if (typeof authorPublic.teachingLanguages === 'string' && authorPublic.teachingLanguages.trim()) {
      return [authorPublic.teachingLanguages.trim()];
    }

    const single =
      publicData?.teachingLanguage ||
      authorPublic.teachingLanguage ||
      publicData?.languagePreference ||
      authorPublic.languagePreference;
    if (single) {
      const s = String(single).toLowerCase().trim();
      if (s === 'ar' || s === 'arabic') return ['arabic'];
      if (s === 'en' || s === 'english') return ['english'];
      if (s === 'fr' || s === 'french') return ['french'];
    }

    const available = getAvailableTeachingLanguages(spokenLanguage);
    if (Array.isArray(available) && available.length > 0) {
      return available.map(opt => opt.value);
    }

    return ['arabic'];
  }, [teachingLanguages, author, publicData, spokenLanguage]);

  const teachingLanguageLabels = useMemo(
    () =>
      (Array.isArray(rawTeachingLanguages) ? rawTeachingLanguages : [rawTeachingLanguages])
        .map(item => {
          const key = (typeof item === 'string' ? item : item?.key || item?.value || item?.option || '').toLowerCase().trim();
          if (key === 'arabic' || key === 'ar') return language === 'ar' ? 'العربية' : 'Arabic';
          if (key === 'english' || key === 'en') return language === 'ar' ? 'الإنجليزية' : 'English';
          if (key === 'french' || key === 'fr') return language === 'ar' ? 'الفرنسية' : 'French';
          return typeof item === 'object' ? (language === 'ar' ? item?.label_ar : item?.label) : item;
        })
        .filter(Boolean)
        .join(', '),
    [language, rawTeachingLanguages]
  );

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = useMemo(() => {
    if (!setActiveListing) return null;
    return {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    };
  }, [setActiveListing, currentListing.id]);

  // Memoize link click handlers to prevent unnecessary function creation
  const handleLinkClick = useCallback(e => {
    if (isInquiryModalOpen) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [isInquiryModalOpen]);

  const handleLinkMouseDown = useCallback(e => {
    if (isInquiryModalOpen) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [isInquiryModalOpen]);

  const handleLinkKeyDown = useCallback(e => {
    if (isInquiryModalOpen) {
      // Prevent Enter/Space from activating the link while modal is open
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }, [isInquiryModalOpen]);

  // Memoize inquiry submit handler
  const inquirySubmitHandler = useCallback(values => {
    if (typeof onSubmitInquiry === 'function') {
      const maybePromise = onSubmitInquiry(currentListing, values);
      // Close modal after successful submit
      return Promise.resolve(maybePromise).then(() => setInquiryModalOpen(false));
    }
    // eslint-disable-next-line no-console
    console.warn(
      'Inquiry submit handler missing. Please pass onSubmitInquiry prop to ListingCard.'
    );
    setInquiryModalOpen(false);
    return Promise.resolve();
  }, [onSubmitInquiry, currentListing]);

  // Memoize manage disable scrolling function
  const manageDisableScrollingSafe = useCallback(
    (...args) => {
      if (typeof onManageDisableScrolling === 'function') {
        onManageDisableScrolling(...args);
      }
    },
    [onManageDisableScrolling]
  );

  // Memoize video button handlers
  const handleVideoButtonClick = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setVideo(true);
  }, []);

  const handleVideoButtonMouseDown = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleVideoButtonKeyDown = useCallback(e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      setVideo(true);
    }
  }, []);

  // Memoize inquiry button handlers
  const handleInquiryButtonClick = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (currentUser) {
      setInquiryModalOpen(true);
    } else {
      const targetListingPageUrl = createResourceLocatorString(
        'ListingPage',
        routeConfiguration,
        { id, slug },
        { inquiry: 'true' }
      );
      const signupUrl = createResourceLocatorString('SignupPage', routeConfiguration, {}, {});
      history.push(signupUrl, { from: targetListingPageUrl });
    }
  }, [currentUser, routeConfiguration, history, id, slug]);

  const listingTitle = currentListing?.attributes?.title;
  const authorDisplayName = authorName;

  return (
    <>
      <NamedLink
        className={classes}
        name="ListingPage"
        params={{ id, slug }}
        onClick={handleLinkClick}
        onMouseDown={handleLinkMouseDown}
        onKeyDown={handleLinkKeyDown}
      >
        <div className={css.cardWrapper} {...setActivePropsMaybe}>
          <div className={css.header}>
            <div className={css.headerLeft}>
              <div className={css.avatarCol}>
                <ListingCardImage
                  renderSizes={renderSizes}
                  title={title}
                  currentListing={currentListing}
                  config={config}
                  setActivePropsMaybe={null}
                  aspectWidth={4}
                  aspectHeight={3}
                  variantPrefix={variantPrefix}
                  style={cardStyle}
                  showListingImage={showListingImage}
                  priorityImage={priorityImage}
                />
                {/* <span className={css.onlineStatus} /> */}
                {videoIntroduction && (
                  <button
                    type="button"
                    className={css.avtarPlayIcon}
                    aria-label={intl.formatMessage({ id: 'ListingCard.playVideo' })}
                    onClick={handleVideoButtonClick}
                    onMouseDown={handleVideoButtonMouseDown}
                    onKeyDown={handleVideoButtonKeyDown}
                  >
                    <IconCard type="play" />
                  </button>
                )}
              </div>

              <div className={css.headerMain}>
                <div className={css.nameRow}>
                  <h3 className={css.name}>
                    {authorName}
                  </h3>
                </div>
                <div className={css.metaRow}>
                  {teachingLanguageLabels ? (
                    <div className={css.languageItem}>
                      <span className={css.languageIconWrap}>
                        <IconCard type="speaks" />
                      </span>
                      <span className={css.languageText}>
                        <span className={css.speaksLabel}>
                          <FormattedMessage id="ListingPage.teachesInLabel" defaultMessage="Teaches in" />:{' '}
                        </span>
                        {teachingLanguageLabels}
                      </span>
                    </div>
                  ) : null}
                  <div className={css.languageItem}>
                    <span className={css.languageIconWrap}>
                      <IconCard type="speaks" />
                    </span>
                    <span className={css.languageText}>
                      <span className={css.speaksLabel}>
                        <FormattedMessage id="ListingPage.speaksLabel" defaultMessage="Speaks" />:{' '}
                      </span>
                      {spokenLanguageLabels}
                    </span>
                  </div>
                </div>
                {teachFromZero && (
                  <div className={css.badgeRow}>
                    <span className={css.scratchBadge}>
                      <FormattedMessage id="ListingCard.badge.teachesFromScratch" defaultMessage="Teaches from Scratch" />
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className={css.mobileMetaRow}>
              {teachingLanguageLabels ? (
                <div className={css.languageItem}>
                  <span className={css.languageIconWrap}>
                    <IconCard type="speaks" />
                  </span>
                  <span className={css.languageText}>
                    <span className={css.speaksLabel}>
                      <FormattedMessage id="ListingPage.teachesInLabel" defaultMessage="Teaches in" />:{' '}
                    </span>
                    {teachingLanguageLabels}
                  </span>
                </div>
              ) : null}
              <div className={css.languageItem}>
                <span className={css.languageIconWrap}>
                  <IconCard type="speaks" />
                </span>
                <span className={css.languageText}>
                  <span className={css.speaksLabel}>
                    <FormattedMessage id="ListingPage.speaksLabel" defaultMessage="Speaks" />:{' '}
                  </span>
                  {spokenLanguageLabels}
                </span>
              </div>
            </div>

            <div className={css.actionsCol}>
              <span className={css.primaryBtn}>
                <FormattedMessage id="ListingCard.bookTrial" />
              </span>
              <Button
                type="button"
                className={css.secondaryBtn}
                onClick={handleInquiryButtonClick}
              >
                <FormattedMessage id="ListingCard.sendMessage" />
              </Button>
            </div>
          </div>
          <div className={classNames(css.statsRow, { [css.hasThreeStats]: ratingCount > 0 })}>
            <div className={css.statBox}>
              <div className={css.ratingValue}>
                <FormattedMessage id="ListingCard.experience" />
              </div>
              <div className={css.statValue}>{experienceLabel}</div>
            </div>
            <div className={css.statBox}>
              <div className={css.statValueStrong}>
                <PriceMaybe
                  price={price}
                  publicData={publicData}
                  intl={intl}
                  listingTypeConfig={foundListingTypeConfig}
                  listing={listing}
                  author={author}
                />
              </div>
              <div className={css.statValue}>
                <FormattedMessage id="ListingCard.lessonDuration" />
              </div>
            </div>
            {ratingCount > 0 && (
              <div className={css.statBox}>
                <div className={css.statIconRow}>
                  <IconReviewStar isFilled className={css.star} label="rating" />
                  <span className={css.ratingValue}>{ratingText}</span>
                </div>
                <div className={css.statValue}>
                  {intl.formatMessage({ id: 'ListingCard.reviewsCount' }, { count: ratingCount || 0 })}
                </div>
              </div>
            )}
          </div>
          <div className={css.description}>
            {displayBio ? <AuthorBioSection bio={displayBio} /> : null}
          </div>
          <div className={css.actionsColMobile}>
            <span className={css.primaryBtn}>
              <FormattedMessage id="ListingCard.bookTrial" />
            </span>
            <Button
              type="button"
              className={css.secondaryBtn}
              onClick={handleInquiryButtonClick}
            >
              <FormattedMessage id="ListingCard.sendMessage" />
            </Button>
          </div>
        </div>
      </NamedLink>
      {/* Inquiry modal rendered outside of NamedLink to avoid navigation while interacting */}
      {isInquiryModalOpen ? (
        <Modal
          id={`ListingCard.inquiry.${id}`}
          contentClassName={css.inquiryModalContent}
          isOpen
          onClose={() => setInquiryModalOpen(false)}
          usePortal
          onManageDisableScrolling={manageDisableScrollingSafe}
        >
          <InquiryForm
            className={css.inquiryForm}
            submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
            listingTitle={listingTitle}
            authorDisplayName={authorDisplayName}
            sendInquiryError={sendInquiryError}
            onSubmit={inquirySubmitHandler}
            inProgress={sendInquiryInProgress}
          />
        </Modal>
      ) : null}
      {playVideo ? (
        <Modal
          id={`ListingCard.videoModal.${id}`}
          isOpen
          onClose={() => setVideo(false)}
          onManageDisableScrolling={manageDisableScrollingSafe}
          usePortal
          containerClassName={css.videoModalContainer}
          contentClassName={css.videoModalContent}
          scrollLayerClassName={css.videoModalScrollLayer}
        >
          <>
            <div className={css.modalHeader}>
              <div className={css.modalTitle}>{title}</div>
              <button
                type="button"
                className={css.modalClose}
                onClick={() => setVideo(false)}
                aria-label={intl.formatMessage({ id: 'ListingCard.close' })}
              >
                <IconCard type="cross" />
              </button>
            </div>
            <video
              key="listing-video"
              src={videoIntroduction}
              autoPlay
              controls
              controlsList="nodownload"
              onContextMenu={e => e.preventDefault()}
            />
          </>
        </Modal>
      ) : null}
    </>
  );
};

export const ListingCard = memo(ListingCardComponent);

export default ListingCard;
